import omitProps from '@hocs/omit-props';
import withLifecycle from '@hocs/with-lifecycle';
import { func } from 'prop-types';
import {
  all,
  allPass,
  always,
  any,
  assoc,
  dropLastWhile,
  equals,
  filter,
  find,
  hasIn,
  last,
  map,
  not,
  prop,
  propEq,
  reduce,
  T,
} from 'ramda';
import { isObject, isString } from 'ramda-adjunct';
import { injectIntl } from 'react-intl';
import { connect } from 'react-refetch';
import { compose, setDisplayName, setPropTypes, withHandlers, withPropsOnChange, withStateHandlers } from 'recompose';
import { callOrUse } from '../../callOrUse';
import { withFolderInfoMP } from '../../hooks/useMediaManagerSDK/useMediaManagerSDK';
import { withFilesImporter } from '../../hooks/useModal/withFilesImporter';
import { MediaManagerSDKFolder } from '../../media-manager-sdk';
import {
  canFolderWithIdBeNavigated,
  getTranslatedItemName,
  isWixVideoLibraryLink,
} from '../../media-manager-sdk/items';
import { ejectIntl } from '../ejectIntl';
import { withRootFolder } from '../withRootFolder';
import { withSources } from '../withSources';
import {
  IState,
  IWithFolderPathInner,
  IWithFolderPathOuter,
  TFolderIdValidationResult,
  TFolderPathConfig,
} from './withFolderPath.types';
import { captureException } from '@sentry/browser';

type TGetFolderPathConfig = ((props: any) => TFolderPathConfig) | TFolderPathConfig;

export const withFolderPath = (getFolderPathConfig: TGetFolderPathConfig = {}, getFolderPathConfigDeps?: string[]) =>
  compose<IWithFolderPathInner, IWithFolderPathOuter>(
    setPropTypes({
      navigateToFolder: func.isRequired,
    }),
    setDisplayName('withFolderPath'),
    withRootFolder,
    withSources,
    withPropsOnChange(getFolderPathConfigDeps || T, (props) => ({
      withFolderPathConfig: callOrUse(getFolderPathConfig, props),
    })),
    withPropsOnChange(
      ['withFolderPathConfig', 'folderId', 'rootFolder'],
      ({ withFolderPathConfig, folderId, rootFolder }) => ({
        folderId: folderId || rootFolder.id,
        withFolderPathConfig: {
          ...withFolderPathConfig,
          ...(hasIn('validateFolderIdIfCanGetFolderInfo', withFolderPathConfig)
            ? {}
            : { validateFolderIdIfCanGetFolderInfo: always({ isValid: true }) }),
          ...(hasIn('isPathFolderValid', withFolderPathConfig) ? {} : { isPathFolderValid: true }),
        },
      }),
    ),
    withStateHandlers<
      IState,
      {},
      IWithFolderPathOuter & { folderId?: string; withFolderPathConfig: TFolderPathConfig }
    >(
      ({ initialFolderPath, folderId, withFolderPathConfig }) => ({
        isPathFolderValid: withFolderPathConfig.isPathFolderValid,
        folderPath: initialFolderPath || [folderId],
        newFolderIdWithAttemptToNavigate: null,
      }),
      {
        setPathFolderValid: () => (isPathFolderValid) => ({ isPathFolderValid }),
        setFolderPath: () => (folderPath) => ({ folderPath }),
        saveNavigationAttempt: () => (newFolderIdWithAttemptToNavigate) => ({
          newFolderIdWithAttemptToNavigate,
        }),
        clearNavigationAttempts: () => () => ({ newFolderIdWithAttemptToNavigate: null }),
      },
    ),
    withFilesImporter,
    withHandlers({
      openFolderById:
        ({ navigateToFolder, source }) =>
        (folderId, folderSource = source, searchQuery, fileId) => {
          return navigateToFolder(folderId, folderSource, searchQuery, fileId);
        },
      openFolder:
        ({
          navigateToFolder,
          setFolderPath,
          folderPath,
          saveNavigationAttempt,
          clearNavigationAttempts,
          source,
          isBoards,
          openFilesImporterModal,
        }) =>
        (folder) => {
          const { id, source: folderSource, parentId } = folder;
          if (isWixVideoLibraryLink(folder)) {
            openFilesImporterModal();
            return;
          }

          if (!canFolderWithIdBeNavigated(id)) {
            saveNavigationAttempt(id);

            return;
          }

          const shouldStayInCurrentFolderPath = isBoards;

          navigateToFolder(id, shouldStayInCurrentFolderPath ? source : folderSource, '');
          clearNavigationAttempts();

          if (last(folderPath) && last(folderPath).id === parentId) {
            // if path is not empty and new folder is child of current folder
            // push it to path
            setFolderPath([...folderPath, folder]);
          } else if (
            all(isObject, folderPath) &&
            any(allPass([propEq('id', id), propEq('source', folderSource)]), folderPath)
          ) {
            // if trying to open folder that is part of current folder path
            // keep folder path until that folder and remove all others
            setFolderPath(dropLastWhile<MediaManagerSDKFolder>((item) => not(propEq('id', id, item)), folderPath));
          } else if (shouldStayInCurrentFolderPath) {
            setFolderPath([...folderPath, folder]);
          } else {
            // set new folder path
            setFolderPath(parentId ? [parentId, folder] : [folder]);
          }
        },
      openRootFolder:
        ({ setFolderPath, navigateToFolder, rootFolder }) =>
        () => {
          navigateToFolder(undefined, rootFolder.source);
          setFolderPath([rootFolder]);
        },
    }),
    withHandlers({
      updateFolderPath:
        ({ setFolderPath, folderPath, isUnsplash }) =>
        (folderId, folderSource) => {
          if (isUnsplash) {
            setFolderPath([null]);
            return;
          }

          if (
            all(isObject, folderPath) &&
            any(allPass([propEq('id', folderId), propEq('source', folderSource)]), folderPath)
          ) {
            setFolderPath(dropLastWhile<MediaManagerSDKFolder>((item) => item.id !== folderId, folderPath));
          } else {
            setFolderPath([folderId]);
          }
        },
      openJustCreatedFolder:
        ({ newFolderIdWithAttemptToNavigate, openFolder }) =>
        (tempNewFolderId, folder) => {
          if (equals(tempNewFolderId, newFolderIdWithAttemptToNavigate)) {
            openFolder(folder);
          }
        },
    }),
    withLifecycle({
      onDidMount({ updateFolderPath, isUnsplash, folderId, source }) {
        if (isUnsplash && folderId) {
          updateFolderPath(null, source);
        }
      },
      onDidUpdate(
        { folderId: prevFolderId, source: prevSource },
        { source, folderId, updateFolderPath, folderPath, isUnsplash },
      ) {
        const lastUsedFolder = last(folderPath);
        const isNewFolderId = prevFolderId !== folderId;

        if (isUnsplash && !folderId && isNewFolderId) {
          updateFolderPath(null, source);
          return;
        }

        if ((prevSource !== source || isNewFolderId) && !(lastUsedFolder && lastUsedFolder.id === folderId)) {
          updateFolderPath(folderId, source);
        }
      },
    }),
    injectIntl,
    withFolderInfoMP,
    connect<any>((props) => {
      const {
        folderPath,
        rootFolder,
        isPathFolderValid,
        setPathFolderValid,
        openRootFolder,
        folderInfoMP,
        setFolderPath,
        source,
        isMediaFromWix,
        withFolderPathConfig: { shouldLoadFolders = true, validateFolderIdIfCanGetFolderInfo },
        intl: { formatMessage },
      } = props;

      if (!shouldLoadFolders) {
        return {
          folderData: {
            value: null,
          },
        };
      }

      const folderToLoad: string = find(isString, folderPath);

      if (!folderToLoad) {
        return {};
      }

      const { fallbackFolder, isValid }: TFolderIdValidationResult = validateFolderIdIfCanGetFolderInfo(
        folderToLoad,
        props,
      );

      return isValid
        ? {
            folderData: {
              comparison: folderToLoad,
              force: isMediaFromWix,
              value: () => {
                if (folderToLoad === rootFolder.id) {
                  return rootFolder;
                }

                if (folderToLoad) {
                  /* TODO:
                   Try-catching because there's a bug somewhere when calculating folderPath.
                   It's put in react state (which is async) and gets recalculated on updates.
                   A race condition happens, which makes this code think we're still
                   at the previous url.
                   */
                  try {
                    return folderInfoMP(folderToLoad, source)
                      .then(prop('data'))
                      .then((item) => assoc('name', getTranslatedItemName(item, formatMessage), item));
                  } catch (e) {
                    console.error(e);
                    captureException(e);
                  }
                }

                return null;
              },
              andThen: (folderData: MediaManagerSDKFolder) => {
                if (folderData) {
                  setFolderPath(
                    reduce(
                      (acc, folder) => {
                        if (folder === folderToLoad) {
                          if (folderData.parentId) {
                            return [...acc, folderData.parentId, folderData];
                          }
                          return [...acc, folderData];
                        }
                        return [...acc, folder];
                      },
                      [],
                      folderPath,
                    ),
                  );

                  if (!isPathFolderValid) {
                    setPathFolderValid(true);
                  }
                }

                return {};
              },
              andCatch: () => {
                openRootFolder();
                setPathFolderValid(true);

                return {};
              },
            },
          }
        : fallbackFolder
        ? {
            folderData: {
              value: Promise.resolve(fallbackFolder),
              andThen: (folderData) => {
                const nextFolderPath = reduce(
                  (nextFolderPath, folder) => [...nextFolderPath, folder === folderToLoad ? folderData : folder],
                  [],
                  folderPath,
                );

                setFolderPath(nextFolderPath);

                return {};
              },
            },
          }
        : {};
    }),
    ejectIntl,
    withPropsOnChange(['folderPath', 'rootFolder', 'folderId'], ({ folderPath, rootFolder, folderId }) => {
      let newFolderPath = filter(isObject, folderPath);

      if (!any(propEq('id', rootFolder.id), newFolderPath)) {
        newFolderPath = [rootFolder, ...newFolderPath];
      }

      const getCurrentFolder = rootFolder.id !== null ? propEq('id', folderId) : propEq('name', rootFolder.name);

      return {
        folderPath: newFolderPath,
        currentFolder: find(getCurrentFolder, newFolderPath) || rootFolder || null,
      };
    }),
    withHandlers({
      updateFolderInPath:
        ({ folderPath, setFolderPath }) =>
        (id, newProps) => {
          setFolderPath(
            map((folderFromPath: MediaManagerSDKFolder) => {
              if (folderFromPath.id !== id) {
                return folderFromPath;
              }

              return { ...folderFromPath, ...newProps };
            }, folderPath),
          );
        },
    }),
    omitProps('withFolderPathConfig', 'folderData'),
  );
